<template>
  <el-aside class="kefu h-100%" width="300px">
    <div class="kefu-filter">
      <div class="kefu-filter__title">
        <span>会话</span>
        <em>{{ kefuStore.getConversationList.length }}</em>
      </div>
      <el-select
        v-model="filterMode"
        class="w-100%"
        size="small"
        @change="onFilterChange"
      >
        <el-option label="全部会话" value="all" />
        <el-option label="仅平台" value="platform" />
        <el-option label="指定店铺" value="shop" />
      </el-select>
      <el-select
        v-if="filterMode === 'shop'"
        v-model="selectedShopId"
        class="w-100% mt-8px"
        size="small"
        filterable
        clearable
        placeholder="选择店铺"
        @change="onShopPick"
      >
        <el-option
          v-for="s in shopOptions"
          :key="s.id"
          :label="s.name"
          :value="s.id"
        />
      </el-select>
    </div>
    <div class="kefu-list">
      <div
        v-for="item in kefuStore.getConversationList"
        :key="item.id"
        :class="{
          active: item.id === activeConversationId,
          pinned: item.adminPinned,
          unread: item.adminUnreadMessageCount > 0
        }"
        class="kefu-conversation"
        @click="openRightMessage(item)"
        @contextmenu.prevent="rightClick($event as PointerEvent, item)"
      >
        <div class="kefu-conversation__avatar">
          <el-badge
            :hidden="item.adminUnreadMessageCount === 0"
            :max="99"
            :value="item.adminUnreadMessageCount"
          >
            <el-avatar :size="40" :src="item.userAvatar || undefined" alt="avatar" />
          </el-badge>
        </div>
        <div class="kefu-conversation__body">
          <div class="kefu-conversation__row">
            <span class="username" :class="{ 'is-unread': item.adminUnreadMessageCount > 0 }">
              {{ item.userNickname }}
            </span>
            <span class="time">{{ lastMessageTimeMap.get(item.id) ?? '' }}</span>
          </div>
          <div class="kefu-conversation__meta">
            <el-tag
              size="small"
              :type="!item.shopId || Number(item.shopId) === 0 ? 'success' : 'danger'"
              effect="plain"
            >
              {{ item.shopName || (!item.shopId || Number(item.shopId) === 0 ? '平台' : '店铺') }}
            </el-tag>
            <el-tag
              v-if="item.csBotStatus === 'HUMAN'"
              size="small"
              type="warning"
              effect="plain"
            >
              待人工
            </el-tag>
            <el-tag
              v-else-if="!item.csBotStatus || item.csBotStatus === 'BOT'"
              size="small"
              type="info"
              effect="plain"
            >
              AI
            </el-tag>
            <Icon v-if="item.adminPinned" class="pin-icon" icon="ep:top" />
          </div>
          <div
            v-dompurify-html="
              getConversationDisplayText(item.lastMessageContentType, item.lastMessageContent)
            "
            class="last-message"
          ></div>
        </div>
      </div>
      <el-empty
        v-if="!kefuStore.getConversationList.length"
        class="kefu-list-empty"
        description="暂无会话"
        :image-size="72"
      />
    </div>

    <!-- 右键，进行操作（类似微信） -->
    <ul v-show="showRightMenu" :style="rightMenuStyle" class="right-menu-ul">
      <li
        v-show="!rightClickConversation.adminPinned"
        class="flex items-center"
        @click.stop="updateConversationPinned(true)"
      >
        <Icon class="mr-5px" icon="ep:top" />
        置顶会话
      </li>
      <li
        v-show="rightClickConversation.adminPinned"
        class="flex items-center"
        @click.stop="updateConversationPinned(false)"
      >
        <Icon class="mr-5px" icon="ep:bottom" />
        取消置顶
      </li>
      <li class="flex items-center" @click.stop="deleteConversation">
        <Icon class="mr-5px" color="red" icon="ep:delete" />
        删除会话
      </li>
      <li class="flex items-center" @click.stop="closeRightMenu">
        <Icon class="mr-5px" color="red" icon="ep:close" />
        取消
      </li>
    </ul>
  </el-aside>
</template>

<script lang="ts" setup>
import { KeFuConversationApi, KeFuConversationRespVO } from '@/api/mall/promotion/kefu/conversation'
import { getShopSimpleList } from '@/api/merchant/shop-manager'
import { useEmoji } from './tools/emoji'
import { formatPast } from '@/utils/formatTime'
import { KeFuMessageContentTypeEnum } from './tools/constants'
import { useAppStore } from '@/store/modules/app'
import { useMallKefuStore } from '@/store/modules/mall/kefu'
import { jsonParse } from '@/utils'

defineOptions({ name: 'KeFuConversationList' })

const message = useMessage() // 消息弹窗
const appStore = useAppStore()
const kefuStore = useMallKefuStore() // 客服缓存
const { replaceEmoji } = useEmoji()
const activeConversationId = ref(-1) // 选中的会话
const collapse = computed(() => appStore.getCollapse) // 折叠菜单

type FilterMode = 'all' | 'platform' | 'shop'
const filterMode = ref<FilterMode>('all')
const selectedShopId = ref<number | undefined>()
const shopOptions = ref<{ id: number; name: string }[]>([])

const loadShopOptions = async () => {
  try {
    const list = (await getShopSimpleList()) || []
    shopOptions.value = (list as any[]).map((s) => ({
      id: Number(s.id),
      name: s.name || s.shopName || `店铺#${s.id}`
    }))
  } catch {
    shopOptions.value = []
  }
}

const onFilterChange = async () => {
  if (filterMode.value === 'all') {
    selectedShopId.value = undefined
    await kefuStore.setConversationList(null)
  } else if (filterMode.value === 'platform') {
    selectedShopId.value = undefined
    await kefuStore.setConversationList(0)
  } else {
    if (!shopOptions.value.length) {
      await loadShopOptions()
    }
    if (selectedShopId.value != null) {
      await kefuStore.setConversationList(selectedShopId.value)
    } else {
      // -1：占位筛选，WS 暂不接入任何会话，直到选中具体店
      kefuStore.$patch({ conversationList: [], filterShopId: -1 })
    }
  }
  calculationLastMessageTime()
}

const onShopPick = async () => {
  if (selectedShopId.value == null) {
    kefuStore.$patch({ conversationList: [], filterShopId: -1 })
    return
  }
  await kefuStore.setConversationList(selectedShopId.value)
  calculationLastMessageTime()
}

/** 计算消息最后发送时间距离现在过去了多久 */
const lastMessageTimeMap = ref<Map<number, string>>(new Map<number, string>())
const calculationLastMessageTime = () => {
  kefuStore.getConversationList?.forEach((item) => {
    lastMessageTimeMap.value.set(item.id, formatPast(item.lastMessageTime, 'YYYY-MM-DD'))
  })
}
defineExpose({ calculationLastMessageTime })

/** 打开右侧的消息列表 */
const emits = defineEmits<{
  (e: 'change', v: KeFuConversationRespVO): void
}>()
const openRightMessage = (item: KeFuConversationRespVO) => {
  // 同一个会话则不处理
  if (activeConversationId.value === item.id) {
    return
  }
  activeConversationId.value = item.id
  emits('change', item)
}

/** 获得消息类型 */
const getConversationDisplayText = computed(
  () => (lastMessageContentType: number, lastMessageContent: string) => {
    switch (lastMessageContentType) {
      case KeFuMessageContentTypeEnum.SYSTEM:
        return '[系统消息]'
      case KeFuMessageContentTypeEnum.VIDEO:
        return '[视频消息]'
      case KeFuMessageContentTypeEnum.IMAGE:
        return '[图片消息]'
      case KeFuMessageContentTypeEnum.PRODUCT:
        return '[商品消息]'
      case KeFuMessageContentTypeEnum.ORDER:
        return '[订单消息]'
      case KeFuMessageContentTypeEnum.VOICE:
        return '[语音消息]'
      case KeFuMessageContentTypeEnum.TEXT:
        return replaceEmoji(jsonParse(lastMessageContent).text || lastMessageContent)
      default:
        return ''
    }
  }
)

//======================= 右键菜单 =======================
const showRightMenu = ref(false) // 显示右键菜单
const rightMenuStyle = ref<any>({}) // 右键菜单 Style
const rightClickConversation = ref<KeFuConversationRespVO>({} as KeFuConversationRespVO) // 右键选中的会话对象

/** 打开右键菜单 */
const rightClick = (mouseEvent: PointerEvent, item: KeFuConversationRespVO) => {
  rightClickConversation.value = item
  // 显示右键菜单
  showRightMenu.value = true
  rightMenuStyle.value = {
    top: mouseEvent.clientY - 110 + 'px',
    left: collapse.value ? mouseEvent.clientX - 80 + 'px' : mouseEvent.clientX - 210 + 'px'
  }
}
/** 关闭右键菜单 */
const closeRightMenu = () => {
  showRightMenu.value = false
}

/** 置顶会话 */
const updateConversationPinned = async (adminPinned: boolean) => {
  // 1. 会话置顶/取消置顶
  await KeFuConversationApi.updateConversationPinned({
    id: rightClickConversation.value.id,
    adminPinned
  })
  message.notifySuccess(adminPinned ? '置顶成功' : '取消置顶成功')
  // 2. 关闭右键菜单，更新会话列表
  closeRightMenu()
  await kefuStore.updateConversation(rightClickConversation.value.id)
}

/** 删除会话 */
const deleteConversation = async () => {
  // 1. 删除会话
  await message.confirm('您确定要删除该会话吗？')
  await KeFuConversationApi.deleteConversation(rightClickConversation.value.id)
  // 2. 关闭右键菜单，更新会话列表
  closeRightMenu()
  kefuStore.deleteConversation(rightClickConversation.value.id)
}

/** 监听右键菜单的显示状态，添加点击事件监听器 */
watch(showRightMenu, (val) => {
  if (val) {
    document.body.addEventListener('click', closeRightMenu)
  } else {
    document.body.removeEventListener('click', closeRightMenu)
  }
})

const timer = ref<any>()
/** 初始化 */
onMounted(() => {
  loadShopOptions()
  timer.value = setInterval(calculationLastMessageTime, 1000 * 10) // 十秒计算一次
})
/** 组件卸载前 */
onBeforeUnmount(() => {
  clearInterval(timer.value)
})
</script>

<style lang="scss" scoped>
.kefu {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--kefu-list-bg, #f8fafc);
  border-right: 1px solid var(--kefu-border, #e8ecf1);

  .kefu-filter {
    flex-shrink: 0;
    padding: 14px 14px 10px;
    background: var(--kefu-list-bg, #f8fafc);
    border-bottom: 1px solid var(--kefu-border, #e8ecf1);

    &__title {
      display: flex;
      align-items: baseline;
      gap: 8px;
      margin-bottom: 10px;
      font-size: 15px;
      font-weight: 600;
      color: var(--kefu-text, #1f2937);

      em {
        font-style: normal;
        font-size: 12px;
        font-weight: 500;
        color: var(--kefu-muted, #8a94a6);
      }
    }
  }

  .kefu-list {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }

  .kefu-list-empty {
    padding-top: 48px;
  }

  .kefu-conversation {
    position: relative;
    display: flex;
    gap: 10px;
    min-height: 76px;
    padding: 10px 14px;
    cursor: pointer;
    border-bottom: 1px solid rgb(232 236 241 / 70%);
    transition: background-color 0.15s ease;

    &:hover {
      background: rgb(30 58 95 / 4%);
    }

    &.active {
      background: var(--kefu-accent-soft, rgb(30 58 95 / 10%));

      &::before {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 3px;
        background: var(--kefu-accent, #1e3a5f);
        content: '';
      }
    }

    &.pinned:not(.active) {
      background: rgb(245 158 11 / 6%);
    }

    &__avatar {
      flex-shrink: 0;
      padding-top: 2px;
    }

    &__body {
      flex: 1;
      min-width: 0;
    }

    &__row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
    }

    &__meta {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 4px;
      margin: 4px 0;

      .pin-icon {
        font-size: 12px;
        color: #f59e0b;
      }
    }

    .username {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      font-size: 14px;
      color: var(--kefu-text, #1f2937);
      text-overflow: ellipsis;
      white-space: nowrap;

      &.is-unread {
        font-weight: 600;
      }
    }

    .time {
      flex-shrink: 0;
      font-size: 12px;
      color: var(--kefu-muted, #8a94a6);
    }

    .last-message {
      overflow: hidden;
      font-size: 12px;
      line-height: 1.4;
      color: var(--kefu-muted, #8a94a6);
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .right-menu-ul {
    position: absolute;
    z-index: 20;
    width: 140px;
    padding: 6px;
    margin: 0;
    list-style-type: none;
    background: var(--kefu-panel, #fff);
    border: 1px solid var(--kefu-border, #e8ecf1);
    border-radius: 10px;
    box-shadow: 0 8px 24px rgb(15 23 42 / 12%);

    li {
      padding: 8px 12px;
      font-size: 13px;
      cursor: pointer;
      border-radius: 8px;
      transition: background-color 0.15s;

      &:hover {
        background: var(--kefu-accent-soft, rgb(30 58 95 / 10%));
      }
    }
  }
}
</style>
